import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Logs the admin out: blacklists the JWT server-side, clears the local token
 * and redirects to the auth screen.
 * @param {Function} navigate - react-router navigate function
 * @returns {Promise<void>}
 */
async function performLogout(navigate) {
    try {
        await axios.delete("users/logout");
    } catch (error) {
        // best-effort: log out client-side even if the blacklist call fails
    }
    localStorage.removeItem("creativeAdminToken");
    axios.defaults.headers.common["Isl-Token"] = null;
    toast("Session expirée après 30 min d'inactivité.");
    navigate("/auth");
}

/**
 * (Re)arms the inactivity timer, clearing any previous one.
 * @param {Object} timerRef - Ref holding the active timeout id
 * @param {Function} navigate - react-router navigate function
 * @param {number} timeoutMs - Inactivity window in milliseconds
 * @returns {void}
 */
function armTimer(timerRef, navigate, timeoutMs) {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => performLogout(navigate), timeoutMs);
}

/**
 * Auto-logout hook: after `timeoutMs` of no user activity, blacklists the JWT
 * and redirects to /auth. Mount once in the authenticated layout.
 * @param {number} [timeoutMs] - Inactivity window (default 30 minutes)
 * @returns {void}
 */
export default function useInactivityLogout(timeoutMs = DEFAULT_TIMEOUT_MS) {
    const navigate = useNavigate();

    useEffect(() => {
        const timer = { current: null };
        const onActivity = () => armTimer(timer, navigate, timeoutMs);
        ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
        onActivity();
        return () => {
            ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
            if (timer.current) clearTimeout(timer.current);
        };
    }, [navigate, timeoutMs]);
}
